import {
  Gender,
  MedigapFilterStateInput, MedigapQuoteQuery,
  useSaveMedigapFilterStateMutation
} from '@coverright/data-access/types/medigap';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import { useCallback } from 'react';

type AdditionalData = {
  zip?: string | null;
  tobacco?: boolean | null;
  gender?: string | null;
  age?: number | null;
}

export function useSaveMgFilters(quote: MedigapQuoteQuery['medigapQuote'], additionalData: AdditionalData = {}, delay = 400) {
  const [saveFilters] = useSaveMedigapFilterStateMutation({
    refetchQueries: ['medigapQuote'],
  });

  return useCallback(
    debounce((filters?: Partial<MedigapFilterStateInput>) => {
      const age = filters?.age || quote.medigapFilterState.age || additionalData.age || 65;

      const gender =
        filters?.gender ||
        quote?.medigapFilterState.gender ||
        additionalData.gender ||
        Gender.M;
      const tobacco =
        filters?.tobacco === undefined ?
        quote?.medigapFilterState.tobacco ||
        additionalData.tobacco ||
        false : filters?.tobacco;
      const zip =
        quote?.medigapFilterState.zip || additionalData.zip as string;

      const input = {
        ...omit(quote?.medigapFilterState, '__typename'),
        ...filters,
        age,
        gender: gender as Gender,
        tobacco,
        zip,
        id: quote?.medigapFilterState.id,
      };

      return saveFilters({
        variables: { input },
        optimisticResponse: () => ({
          saveMedigapFilterState: quote?.medigapFilterState.id,
        }),
        update: (cache) => {
          if (quote) {
            cache.modify({
              id: cache.identify(quote?.medigapFilterState),
              fields: {
                age(cached) {
                  return age || cached;
                },
                gender(cached) {
                  return gender || cached;
                },
                tobacco(cached) {
                  return tobacco || cached;
                },
                effectiveDate(cached) {
                  return input.effectiveDate || cached;
                },
                planNames(cached) {
                  return input.planNames || cached;
                },
                companies(cached) {
                  return input.companies || cached;
                },
                planCategory(cached) {
                  return input.planCategory || cached;
                },
              },
            });
          }
        },
      });
    }, delay),
    [saveFilters, delay, quote]
  );
}
